import {
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nOnlyOnMainForm,
	I18nTextField,
	IconButton,
	ImagePickerInput,
	LanguageSingleSelectField,
	LocaleProvider,
	SingleSelectField,
	useDynamicConfig,
	useLocale,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { EventSaveRequest, EventVisibility, visibilityOptions } from "../../types/event";
import { isAllProhibited } from "../../types/eventType";
import { EventTypeSingleSelectField } from "../../ui/EventTypeSingleSelectField";
import { CreatableLocationField } from "./CreatableLocationField";
import {
	CreateEventInitialValues,
	CreateEventTranslationValue,
	SimilarEvents,
	useCreateEvent,
} from "./CreateEventProvider";
import { EventDateTimeFields } from "./EventDateTimeFields";
import { mapEventFormValuesToRequest } from "./eventFormUtils";
import { SimilarEventsField } from "./SimilarEventsField";

interface Props {
	createPath: string;
	detailsPath: string;
	handleSubmit: (request: EventSaveRequest) => Promise<void>;
}

export function CreateEventDrawer({ createPath, detailsPath, handleSubmit }: Props) {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const [open, setOpen] = useState(searchParams.get("create") !== null);
	const { languageSettings } = useDynamicConfig();
	const navigate = useNavigate();
	const { timeZone } = useLocale();
	const { setDrawerValues, mergeFormValues } = useCreateEvent();

	const handleOpen = () => setOpen(true);
	const handleClose = () => {
		setOpen(false);
	};

	const validate = (values: CreateEventInitialValues) => {
		const errors: FormikErrors<CreateEventInitialValues> = {};

		if (!values.eventType) {
			errors.eventType = t("Field is required");
		}
		if (!values.start) {
			errors.start = t("Field is required");
		}
		if (!values.end) {
			errors.end = t("Field is required");
		}
		if (values.end && values.start && values.start >= values.end) {
			errors.end = t("End has to be after start");
		}
		if (!values.primaryLanguage) {
			errors.primaryLanguage = t("Field is required");
		}
		if (values.eventType?.locationRequirement === "required" && !values.location) {
			errors.location = t("Field is required");
		}
		if (values.hasSimilarEvents === SimilarEvents.PENDING || values.hasSimilarEvents === SimilarEvents.TRUE) {
			errors.hasSimilarEvents = t("Field is required");
		}
		if (!values.visibility) {
			errors.visibility = t("Field is required");
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, translations) => {
				const errors: { [K in keyof CreateEventTranslationValue]?: any } = {};
				if (!translations.name) {
					errors.name = t("Field is required");
				}

				return errors;
			}),
		};
	};

	const initialValues: CreateEventInitialValues = {
		translations: [
			{
				locale: languageSettings.fallback,
				name: "",
			},
		],
		timezone: timeZone,
		eventType: null,
		start: null,
		end: null,
		primaryLanguage: null,
		location: null,
		image: null,
		hasSimilarEvents: SimilarEvents.PENDING,
		visibility: "logged_in_users",
	};

	return (
		<>
			<IconButton title={t("Create event")} size="small" color="primary" icon="plus" onClick={handleOpen} />
			<I18nFormDrawer
				renderForm={({ formik, ...drawerProps }) => (
					<I18nFormDrawerContent {...drawerProps} emptyTranslation={{ name: "" }}>
						<I18nTextField name="name" label={t("Name")} required={true} />
						<I18nOnlyOnMainForm>
							<EventTypeSingleSelectField
								name="eventType"
								label={t("Event type")}
								required={true}
								onChange={(e) => {
									const allowed = e.target.value?.allowedVisibilities;
									if (allowed?.length === 1) {
										void formik.setFieldValue("visibility", allowed[0] as EventVisibility);
									} else if (allowed && !allowed.includes(formik.values.visibility)) {
										void formik.setFieldValue("visibility", allowed[0] as EventVisibility);
									}
								}}
							/>
							<LanguageSingleSelectField name="primaryLanguage" label={t("Primary language")} required={true} />
							<LocaleProvider initialTimeZone={timeZone}>
								<EventDateTimeFields />
							</LocaleProvider>
							{(() => {
								const allowed = formik.values.eventType?.allowedVisibilities;
								const options = allowed?.length
									? visibilityOptions.filter((v) => allowed.includes(v))
									: visibilityOptions;
								return (
									<SingleSelectField
										name="visibility"
										label={t("Visibility")}
										required={true}
										options={options}
										getLabel={(v) => t(`eventVisibility.${v}`)}
										getValue={(v) => v}
										disabled={options.length === 1}
									/>
								);
							})()}
							{formik.values.eventType?.locationRequirement !== "prohibited" && (
								<CreatableLocationField
									name="location"
									label={t("Location")}
									clearable={formik.values.eventType?.locationRequirement === "optional"}
									required={formik.values.eventType?.locationRequirement === "required"}
								/>
							)}
							<ImagePickerInput
								label={t("Image")}
								name="image"
								cropImageWithAspect={4}
								aspectRatioOptions={[{ name: "4 / 1", aspect: 4 }]}
								canRemoveImage={true}
							/>
						</I18nOnlyOnMainForm>
						<SimilarEventsField basePath={detailsPath} />
					</I18nFormDrawerContent>
				)}
				open={open}
				onClose={handleClose}
				initialValues={initialValues}
				validate={validate}
				onSubmit={async (values) => {
					if (isAllProhibited(values.eventType)) {
						void handleSubmit(mapEventFormValuesToRequest(mergeFormValues(values)));
					} else {
						setDrawerValues(values);
						navigate(`${createPath}/base-data`);
					}
					return true;
				}}
				title={t("Create event")}
			/>
		</>
	);
}
